import React, { useMemo, useState, useRef } from 'react';
import styled from 'styled-components';
import InfiniteScroll from 'react-infinite-scroller';
import { NotificationClickProp } from '../../components/types';
import { useUserContext } from '../../context/UserContext';
import Spinner from '../../components/Spinner';
import { useChannelContext } from '../../context/ChannelContext';
import { Notification } from '../../context/UserContext/types';
import analytics from '../../services/analytics';
import NotificationFeedItem from './components/NotificationFeedItem';
import EmptyState from './components/EmptyState';
import { Screen } from 'components/layout/Screen';
import { Settings } from 'components/icons';
import Flex from 'components/layout/Flex';
import FeedNavigation, { NavigationTabs } from 'screens/feed/components/FeedNavigation';
import { Routes, useRouterContext } from 'context/RouterContext';

const NOTIFS_PER_PAGE = 10;

const NotificationFeed = styled(Flex)`
  ${({ theme }) => `@media (max-width: ${theme.w.breakpoints.mobile}px) {
    max-height: 100%;
  }`}
  &:not(:last-child) {
    border-bottom: ${({ theme }) => `1px solid ${theme.w.colors.border.main}}`};
  }
  min-height: 300px;
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  width: calc(100% + 18px);
  box-sizing: border-box;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SettingsIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  color: ${({ theme }) => theme.w.colors.gray[100]};
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.w.colors.gray[200]};
  }
`;

export const Feed = ({ onNotificationClick }: NotificationClickProp) => {
  const { notifications: allNotifications, isLoading } = useUserContext();
  const { channelAddress } = useChannelContext();
  const { setRoute } = useRouterContext();
  const [activeTab, setActiveTab] = useState(NavigationTabs.App);
  const [page, setPage] = useState(0);
  const feedRef = useRef<HTMLDivElement | null>(null);

  const handleViewSettings = () => {
    setRoute(Routes.Settings);
  };

  const [channelNotifs, otherNotifs] = useMemo(() => {
    const channel: Notification[] = [];
    const other: Notification[] = [];

    allNotifications.forEach((notif) =>
      notif.appAddress.toLowerCase() === channelAddress.toLowerCase()
        ? channel.push(notif)
        : other.push(notif)
    );

    return [channel, other];
  }, [allNotifications]);

  const tabNotifs = activeTab === NavigationTabs.App ? channelNotifs : otherNotifs;
  const notificationsToShow = useMemo(() => {
    return tabNotifs.slice(0, page * NOTIFS_PER_PAGE + NOTIFS_PER_PAGE);
  }, [activeTab, page, tabNotifs]);

  const handleTabSwitch = (tab: NavigationTabs) => {
    analytics.track('notifications tab switch', { tab });

    if (feedRef.current?.scrollTop) {
      feedRef.current.scrollTop = 0;
    }

    setPage(0);
    setActiveTab(tab);
  };

  return (
    <Screen
      title={'Notifications'}
      navbarActionComponent={
        <SettingsIcon onClick={handleViewSettings}>
          <Settings />
        </SettingsIcon>
      }
    >
      {!!otherNotifs?.length && (
        <FeedNavigation activeTab={activeTab} setActiveTab={handleTabSwitch} />
      )}

      <NotificationFeed ref={feedRef} width={'100%'} direction={'column'} gap={2}>
        <EmptyState show={!isLoading && !notificationsToShow?.length} />
        {isLoading ? (
          <Flex height={150} justifyContent={'center'} alignItems={'center'} pb={3}>
            <Spinner />
          </Flex>
        ) : (
          <InfiniteScroll
            loadMore={() => setPage((currPage) => currPage + 1)}
            useWindow={false}
            hasMore={!!tabNotifs[notificationsToShow.length]}
          >
            {notificationsToShow.map((notification, index) => (
              <NotificationFeedItem
                onNotificationClick={onNotificationClick}
                key={`${index}-${notification.timestamp}`}
                notification={notification}
                showSenderDetails={activeTab === NavigationTabs.Other}
              />
            ))}
          </InfiniteScroll>
        )}
      </NotificationFeed>
    </Screen>
  );
};
